import {
  useEffect,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
  useCallback,
  useMemo,
} from "react";
import Hls from "hls.js";
import { Maximize, Minimize } from "lucide-react";
import { secureRandom } from "./utils/secureRandom";

export interface StreamPlayerHandle {
  loadSource: (url: string) => void;
  pause: () => void;
  play: () => void;
}

interface StreamPlayerProps {
  streamPath: string;
  user: string;
  ip: string;
  caseId: string;
  courtName: string;
  mobile: string;
  location?: string;
  isPaused?: boolean;
  enableFullscreen?: boolean;
  hlsToken?: string;
}

/** ffmpeg can take several seconds to publish index.m3u8 after a switch. */
const MAX_MANIFEST_ATTEMPTS = 20;
const MANIFEST_RETRY_DELAY = 1500;

/* Autoplay policy can reject an UNMUTED play(). Fall back to muted
   playback instead of dying silently on a black frame. */
const safePlay = (video: HTMLVideoElement) => {
  video.play().catch(() => {
    video.muted = true;
    video.play().catch(() => {});
  });
};

const SecureStreamPlayer = forwardRef<StreamPlayerHandle, StreamPlayerProps>(
  function SecureStreamPlayer(
    {
      streamPath,
      user,
      ip,
      caseId,
      courtName,
      mobile,
      location,
      isPaused,
      hlsToken,
      enableFullscreen = true,
    },
    ref,
  ) {
    const videoRef = useRef<HTMLVideoElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const hlsRef = useRef<Hls | null>(null);
    const retryRef = useRef<any>(null);
    const manifestRetryRef = useRef<any>(null);
    const attemptsRef = useRef(0);

    /* requested = what we were TOLD to play (set before loading)
       playing   = what actually parsed a manifest (set on success)
       Keeping them apart is what lets a repeat of the same URL reload
       after a failed first attempt — the switching→live case. */
    const requestedUrlRef = useRef<string | null>(null);
    const playingUrlRef = useRef<string | null>(null);

    const [isFullscreen, setIsFullscreen] = useState(false);
    const [timestamp, setTimestamp] = useState(Date.now());
    const [randPos, setRandPos] = useState({ top: 10, left: 10 });
    const [pulse, setPulse] = useState(0.4);

    const STREAM_PLAYER_CSS = `
    video {
      width: 100%;
      height: 100%;
      display: block;
      background: black;
    }
    video::-webkit-media-controls-fullscreen-button {
      display: none !important;
    }
    video::-webkit-media-controls-enclosure {
      overflow: hidden !important;
    }
    video::-moz-fullscreen-button {
      display: none !important;
    }
    @media (max-width: 768px) {
      .stream-container {
        height: 100vh !important;
        position: relative;
        overflow: hidden;
      }
    }
    @media (min-width: 769px) {
      .stream-container {
        height: 100%;
        overflow: hidden;
      }
      video {
        object-position: top center;
      }
    }
  `;

    const clearRetry = useCallback(() => {
      if (retryRef.current) {
        clearInterval(retryRef.current);
        retryRef.current = null;
      }
      if (manifestRetryRef.current) {
        clearTimeout(manifestRetryRef.current);
        manifestRetryRef.current = null;
      }
    }, []);

    const destroyHls = useCallback(() => {
      if (hlsRef.current) {
        try {
          hlsRef.current.destroy();
        } catch {
          /* noop */
        }
        hlsRef.current = null;
      }
    }, []);

    /* ------------------------------------
        CORE loadStream
    ------------------------------------ */

    const hlsTokenRef = useRef(hlsToken);
    useEffect(() => {
      hlsTokenRef.current = hlsToken;
    }, [hlsToken]);

    const loadStream = useCallback(
      (url: string) => {
        const video = videoRef.current;
        if (!video || !url) return;

        console.log("🎬 loadStream:", url);

        clearRetry();
        destroyHls();
        playingUrlRef.current = null;

        // drop the previous stream's buffer off the media element
        try {
          video.pause();
          video.removeAttribute("src");
          video.load();
        } catch {
          /* noop */
        }

        const scheduleReload = (delay = MANIFEST_RETRY_DELAY) => {
          if (attemptsRef.current >= MAX_MANIFEST_ATTEMPTS) {
            console.warn("giving up on manifest:", url);
            return;
          }
          attemptsRef.current += 1;
          if (manifestRetryRef.current) clearTimeout(manifestRetryRef.current);
          manifestRetryRef.current = setTimeout(() => {
            if (requestedUrlRef.current === url) loadStream(url);
          }, delay);
        };

        if (Hls.isSupported()) {
          const hls = new Hls({
            liveSyncDurationCount: 3,
            enableWorker: true,
            backBufferLength: 30,
            manifestLoadingMaxRetry: 10,
            manifestLoadingRetryDelay: 1000,
            manifestLoadingMaxRetryTimeout: 8000,
            levelLoadingMaxRetry: 6,
            fragLoadingMaxRetry: 6,
            xhrSetup: (xhr, url) => {
              const token = hlsTokenRef.current; // ← always latest token
              console.log("token....", token);
              console.log("url...", url);
              if (token && url.includes("/hls/") && !url.includes("token=")) {
                const sep = url.includes("?") ? "&" : "?";
                xhr.open("GET", `${url}${sep}token=${token}`, true);
              }
            },
          });
          hlsRef.current = hls;

          hls.on(Hls.Events.MEDIA_ATTACHED, () => hls.loadSource(url));

          hls.on(Hls.Events.MANIFEST_PARSED, () => {
            console.log("✅ manifest parsed:", url);
            playingUrlRef.current = url;
            attemptsRef.current = 0;
            safePlay(video);
          });

          hls.on(Hls.Events.ERROR, (_, data) => {
            const d = data.details;

            /* Playlist not written yet (or written with no segments).
               These need a FULL reload — startLoad() cannot fix an
               instance that never parsed a manifest. */
            if (
              d === Hls.ErrorDetails.MANIFEST_LOAD_ERROR ||
              d === Hls.ErrorDetails.MANIFEST_LOAD_TIMEOUT ||
              d === Hls.ErrorDetails.MANIFEST_PARSING_ERROR ||
              d === Hls.ErrorDetails.LEVEL_EMPTY_ERROR
            ) {
              console.warn("manifest not ready:", d, attemptsRef.current);
              scheduleReload();
              return;
            }

            if (!data.fatal) return;
            console.warn("HLS fatal:", data.type, d);

            switch (data.type) {
              case Hls.ErrorTypes.NETWORK_ERROR:
                if (playingUrlRef.current === url) hls.startLoad();
                else scheduleReload();
                break;
              case Hls.ErrorTypes.MEDIA_ERROR:
                hls.recoverMediaError();
                break;
              default:
                scheduleReload(2000);
                break;
            }
          });

          hls.attachMedia(video);
        } else if (video.canPlayType("application/vnd.apple.mpegurl")) {
          // Safari / iOS native HLS
          const onLoaded = () => {
            playingUrlRef.current = url;
            attemptsRef.current = 0;
          };
          video.addEventListener("loadedmetadata", onLoaded, { once: true });
          video.src = url;
          video.load();
          safePlay(video);
        }

        /* ---- watchdog ----
           Never clears itself on success. readyState alone is not
           enough: after a switch the element can hold a stale buffer
           with readyState > 0 while currentTime never advances. */
        let lastTime = -1;
        let stalledTicks = 0;

        retryRef.current = setInterval(() => {
          const v = videoRef.current;
          if (!v) return;

          // a newer load owns the player — this interval is stale
          if (requestedUrlRef.current !== url) {
            clearInterval(retryRef.current);
            retryRef.current = null;
            return;
          }

          const notReady = v.readyState === 0;
          const stalled = !v.paused && v.currentTime === lastTime;
          lastTime = v.currentTime;

          if (!notReady && !stalled && !v.error) {
            stalledTicks = 0; // healthy — keep the interval ALIVE
            return;
          }

          stalledTicks += 1;
          console.log("🔄 stalled", {
            notReady,
            stalled,
            err: v.error?.code,
            stalledTicks,
          });

          // one gentle nudge, but only if a manifest actually parsed
          if (
            stalledTicks === 1 &&
            hlsRef.current &&
            playingUrlRef.current === url
          ) {
            hlsRef.current.startLoad();
            return;
          }

          if (stalledTicks >= 2) {
            stalledTicks = 0;
            loadStream(url); // hard rebuild
          }
        }, 3000);
      },
      [clearRetry, destroyHls],
    );

    /* ------------------------------------
        EXPOSE HANDLE TO PARENT
    ------------------------------------ */
    useImperativeHandle(ref, () => ({
      loadSource(url: string) {
        if (!url) return;
        requestedUrlRef.current = url;
        attemptsRef.current = 0;
        loadStream(url);
      },
      pause() {
        const video = videoRef.current;
        if (!video) return;
        video.pause();
        video.muted = true; // kill audio — prevents leak through the overlay
      },
      play() {
        const video = videoRef.current;
        if (!video) return;
        video.muted = false;
        // only jump if the live edge is genuinely ahead — seeking into a
        // flushed seekable range right after a switch hangs playback
        if (video.seekable.length > 0) {
          const end = video.seekable.end(video.seekable.length - 1);
          if (end - video.currentTime > 1.5) {
            video.currentTime = Math.max(end - 0.5, 0);
          }
        }
        safePlay(video);
      },
    }));

    /* ------------------------------------
        LOAD / SWITCH SOURCE
        Reload when the URL is new OR when the current one was
        requested but never actually parsed (failed first attempt).
    ------------------------------------ */
    useEffect(() => {
      if (!streamPath) {
        requestedUrlRef.current = null;
        playingUrlRef.current = null;
        attemptsRef.current = 0;
        clearRetry();
        destroyHls();
        const v = videoRef.current;
        if (v) {
          v.pause();
          v.muted = true;
        }
        return;
      }

      const alreadyPlaying =
        requestedUrlRef.current === streamPath &&
        playingUrlRef.current === streamPath;
      if (alreadyPlaying) return;

      requestedUrlRef.current = streamPath;
      attemptsRef.current = 0;
      loadStream(streamPath);
    }, [streamPath, loadStream, clearRetry, destroyHls]);

    /* ------------------------------------
        UNMOUNT CLEANUP
    ------------------------------------ */
    useEffect(() => {
      console.log("🟢 SecureStreamPlayer MOUNTED");
      return () => {
        console.log("🔴 SecureStreamPlayer UNMOUNTED — destroying HLS");
        clearRetry();
        destroyHls();
      };
    }, [clearRetry, destroyHls]);

    /* ------------------------------------
        TIMESTAMP
    ------------------------------------ */
    useEffect(() => {
      const i = setInterval(() => setTimestamp(Date.now()), 1000);
      return () => clearInterval(i);
    }, []);

    /* ------------------------------------
        FULLSCREEN
    ------------------------------------ */
    useEffect(() => {
      const handler = () => {
        const video = videoRef.current as HTMLVideoElement & {
          webkitDisplayingFullscreen?: boolean;
        };

        setIsFullscreen(
          !!document.fullscreenElement || !!video?.webkitDisplayingFullscreen,
        );
      };

      document.addEventListener("fullscreenchange", handler);
      document.addEventListener("webkitfullscreenchange", handler);

      return () => {
        document.removeEventListener("fullscreenchange", handler);
        document.removeEventListener("webkitfullscreenchange", handler);
      };
    }, []);

    const toggleFullscreen = async () => {
      const video = videoRef.current;
      const container = containerRef.current;

      if (!video || !container) return;

      try {
        // Exit fullscreen if already active
        if (document.fullscreenElement) {
          await document.exitFullscreen();
          return;
        }

        // Try container fullscreen first.
        // This keeps the custom fullscreen button and overlays inside fullscreen.
        if (container.requestFullscreen) {
          await container.requestFullscreen();
          return;
        }

        // iPhone / older iOS Safari fallback
        const iosVideo = video as HTMLVideoElement & {
          webkitEnterFullscreen?: () => void;
        };

        if (iosVideo.webkitEnterFullscreen) {
          iosVideo.webkitEnterFullscreen();
          return;
        }

        console.warn("Fullscreen is not supported on this browser.");
      } catch (error) {
        console.error("Fullscreen error:", error);

        // iOS native fullscreen fallback
        const iosVideo = video as HTMLVideoElement & {
          webkitEnterFullscreen?: () => void;
        };

        try {
          iosVideo.webkitEnterFullscreen?.();
        } catch (fallbackError) {
          console.error("iOS fullscreen fallback error:", fallbackError);
        }
      }
    };

    /* ------------------------------------
        MOVING WATERMARK
    ------------------------------------ */
    useEffect(() => {
      const interval = setInterval(() => {
        setRandPos({
          top: secureRandom() * 80 + 5,
          left: secureRandom() * 80 + 5,
        });
      }, 2500);
      return () => clearInterval(interval);
    }, []);

    /* ------------------------------------
        PULSE OPACITY
    ------------------------------------ */
    useEffect(() => {
      const interval = setInterval(() => {
        setPulse((p) => (p === 0.4 ? 0.2 : 0.4));
      }, 1500);
      return () => clearInterval(interval);
    }, []);

    /* ------------------------------------
        BLOCK SCREENSHOTS
    ------------------------------------ */
    useEffect(() => {
      const preventScreenshot = (e: KeyboardEvent) => {
        if (
          e.key === "PrintScreen" ||
          (e.ctrlKey && e.key === "p") ||
          (e.metaKey && e.key === "p")
        ) {
          e.preventDefault();
          document.body.style.opacity = "0";
          setTimeout(() => (document.body.style.opacity = "1"), 200);
        }
      };
      window.addEventListener("keydown", preventScreenshot);
      return () => window.removeEventListener("keydown", preventScreenshot);
    }, []);

    /* ------------------------------------
        BLOCK RIGHT CLICK
    ------------------------------------ */
    useEffect(() => {
      const preventContext = (e: MouseEvent) => e.preventDefault();
      document.addEventListener("contextmenu", preventContext);
      return () => document.removeEventListener("contextmenu", preventContext);
    }, []);

    const watermarkText = useMemo(() => {
      return [
        courtName,
        user,
        ip,
        caseId,
        mobile,
        location,
        timestamp ? new Date(timestamp).toLocaleString() : null,
      ]
        .filter(Boolean)
        .join(" | ");
    }, [courtName, user, ip, caseId, mobile, location, timestamp]);

    return (
      <>
        <style>{STREAM_PLAYER_CSS}</style>
        <div ref={containerRef} className="stream-container relative">
          <video
            ref={videoRef}
            controls
            autoPlay
            muted
            playsInline
            controlsList="nodownload nofullscreen noplaybackrate"
            onContextMenu={(e) => e.preventDefault()}
            className="w-full h-full object-contain bg-black"
          />

          {/* Pause overlay — inside containerRef so it shows in fullscreen too */}
          {isPaused && (
            <div className="absolute inset-0 bg-black flex flex-col items-center justify-center z-[200]">
              <div className="flex gap-2 mb-4">
                <span className="block w-3 h-10 bg-white rounded-sm opacity-90" />
                <span className="block w-3 h-10 bg-white rounded-sm opacity-90" />
              </div>
              <p className="text-white text-xl font-semibold">
                Live Stream Temporarily Paused
              </p>
              <p className="text-gray-400 text-sm mt-2">
                Please remain connected; streaming will resume automatically.
              </p>
            </div>
          )}
          {enableFullscreen && (
            <button
              onClick={toggleFullscreen}
              className="absolute bottom-8 right-7.5 z-[100] bg-black/70 text-white p-2 rounded-md hover:bg-black transition"
            >
              {isFullscreen ? <Minimize size={15} /> : <Maximize size={15} />}
            </button>
          )}

          {/* Moving Watermark */}
          <div
            className="absolute z-[9999] pointer-events-none select-none hidden sm:block"
            style={{
              top: `${randPos.top}%`,
              left: `${Math.min(randPos.left, 60)}%`,
              opacity: pulse,
              color: "white",
              fontSize: "16px",
              whiteSpace: "normal",
              maxWidth: "80%",
              textOverflow: "ellipsis",
              textShadow: "1px 1px 3px rgba(0,0,0,0.9)",
            }}
          >
            {watermarkText}
          </div>

          {/* Grid Watermark — fullscreen only */}

          <div
            className="absolute inset-0 z-[9998] pointer-events-none opacity-12 select-none hidden sm:block"
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gridTemplateRows: "repeat(3, 1fr)",
              placeItems: "center",
              padding: "10%",
            }}
          >
            {Array.from({ length: 9 }).map((_, i) => (
              <div
                key={i}
                style={{
                  whiteSpace: "nowrap",
                  fontSize: "14px",
                  color: "rgba(255,255,255,0.35)",
                  textShadow: "1px 1px 4px black",
                  transform: "rotate(-25deg)",
                }}
              >
                {watermarkText}
              </div>
            ))}
          </div>
        </div>
      </>
    );
  },
);

export default SecureStreamPlayer;
